import { Component } from 'react'
import type { ReactNode } from 'react'
import { LBAlert, LBAlertDescription, LBButton } from '@linearb/buzz'
import { trackAction } from '../lib/datadogRum'

const RELOAD_AT_KEY = 'chunkReloadAt'
// Two failures this close together mean the new build is broken, not stale — stop
// reloading and show the error instead of looping.
const RELOAD_WINDOW_MS = 10_000

// A boundary can catch a non-Error throw, so narrow rather than assume `.message`.
const isChunkLoadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error)
  return /dynamically imported module|Importing a module script failed/i.test(
    message
  )
}

// Stamping before every reload — automatic or user-initiated — is what keeps the
// window guard meaningful.
const reloadForNewBuild = (): void => {
  sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()))
  window.location.reload()
}

interface Props {
  children: ReactNode
}

interface State {
  error: unknown
}

// Each deploy uploads to a fresh directory and repoints CloudFront at it, so a tab
// left open across a deploy asks for hashed page chunks that are no longer
// reachable. Reloading picks up the new build.
class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  // Only chunk failures are handled here. Rethrowing anything else keeps its
  // previous behavior instead of parking it behind the reload prompt.
  static getDerivedStateFromError(error: unknown): State {
    if (isChunkLoadError(error)) {
      return { error }
    }
    throw error
  }

  componentDidCatch(): void {
    const lastReload = Number(sessionStorage.getItem(RELOAD_AT_KEY)) || 0
    const willReload = Date.now() - lastReload > RELOAD_WINDOW_MS
    trackAction('chunk_load_failed', {
      path: window.location.pathname,
      willReload
    })
    if (willReload) {
      reloadForNewBuild()
    }
  }

  render(): ReactNode {
    if (!this.state.error) {
      return this.props.children
    }

    return (
      <div className='flex flex-col items-center justify-center py-20 gap-4'>
        <LBAlert variant='destructive' className='max-w-md'>
          <LBAlertDescription className='text-center'>
            This page failed to load. A new version may have been deployed.
          </LBAlertDescription>
        </LBAlert>
        <LBButton variant='outline' onClick={reloadForNewBuild}>
          Reload
        </LBButton>
      </div>
    )
  }
}

export default ChunkErrorBoundary
